(() => {
  const moneyFormatter = (currency = 'AUD') =>
    new Intl.NumberFormat('en-AU', { style: 'currency', currency });

  const pubSubAvailable = () =>
    typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined';

  class CoastdImmersiveMedia {
    constructor(root) {
      this.root = root;
      this.gallery = root.querySelector('.coastd-product-media-gallery');
      this.slider = this.gallery?.querySelector('.coastd-product-media-track');
      this.navigationMode = root.dataset.coastdMediaNavigation || 'dots_counter';
      this.mobileQuery = window.matchMedia('(max-width: 749px)');
      this.scrollFrame = null;
      this.rebuildTimer = null;

      if (!this.gallery || !this.slider) return;

      this.handleScroll = this.queueUpdate.bind(this);
      this.handleResize = this.refresh.bind(this);
      this.handleMediaChange = this.refresh.bind(this);
      this.handleWindowScroll = this.queueHeaderUpdate.bind(this);

      this.slider.addEventListener('scroll', this.handleScroll, { passive: true });
      window.addEventListener('resize', this.handleResize, { passive: true });
      window.addEventListener('scroll', this.handleWindowScroll, { passive: true });
      this.mobileQuery.addEventListener?.('change', this.handleMediaChange);

      this.observer = new MutationObserver(() => {
        window.clearTimeout(this.rebuildTimer);
        this.rebuildTimer = window.setTimeout(() => this.buildNavigation(), 100);
      });
      this.observer.observe(this.slider, { childList: true, subtree: false });

      this.refresh();
      window.setTimeout(() => this.refresh(), 180);
    }

    destroy() {
      this.slider?.removeEventListener('scroll', this.handleScroll);
      window.removeEventListener('resize', this.handleResize);
      window.removeEventListener('scroll', this.handleWindowScroll);
      this.mobileQuery?.removeEventListener?.('change', this.handleMediaChange);
      this.observer?.disconnect();
      window.cancelAnimationFrame(this.scrollFrame);
      window.cancelAnimationFrame(this.headerFrame);
      window.clearTimeout(this.rebuildTimer);
      this.navigation?.remove();
    }

    getItems() {
      return [...this.slider.querySelectorAll(':scope > .coastd-product-media-slide')].filter(
        (item) => !item.classList.contains('is-filtered') && !item.hidden
      );
    }

    refresh() {
      this.setStickyHeaderHeight();

      if (!this.mobileQuery.matches) {
        this.navigation?.remove();
        this.navigation = null;
        return;
      }

      this.buildNavigation();
      this.update();
    }

    queueHeaderUpdate() {
      window.cancelAnimationFrame(this.headerFrame);
      this.headerFrame = window.requestAnimationFrame(() => this.setStickyHeaderHeight());
    }

    setStickyHeaderHeight() {
      const selectors = [
        '.shopify-section-group-header-group',
        '.shopify-section-header-sticky',
        '.section-header',
        'sticky-header',
      ];

      const candidates = [...new Set(
        selectors.flatMap((selector) => [...document.querySelectorAll(selector)])
      )];

      let occupiedBottom = 0;

      candidates.forEach((element) => {
        const styles = window.getComputedStyle(element);
        if (
          styles.display === 'none' ||
          styles.visibility === 'hidden' ||
          Number(styles.opacity) === 0
        ) {
          return;
        }

        const rect = element.getBoundingClientRect();
        const overlapsTopEdge = rect.top <= 2 && rect.bottom > 0;

        if (overlapsTopEdge) occupiedBottom = Math.max(occupiedBottom, rect.bottom);
      });

      const height = Math.max(0, Math.min(Math.ceil(occupiedBottom), 320));
      this.root.style.setProperty('--coastd-sticky-header-height', `${height}px`);
    }

    buildNavigation() {
      if (!this.mobileQuery.matches || this.navigationMode === 'none') {
        this.navigation?.remove();
        this.navigation = null;
        return;
      }

      this.items = this.getItems();

      if (this.items.length <= 1) {
        this.navigation?.remove();
        this.navigation = null;
        return;
      }

      this.navigation?.remove();

      const navigation = document.createElement('div');
      navigation.className = `coastd-immersive-media-nav coastd-immersive-media-nav--${this.navigationMode}`;
      navigation.setAttribute('aria-label', 'Product media navigation');

      const inner = document.createElement('div');
      inner.className = 'coastd-immersive-media-nav__inner';

      const dots = document.createElement('div');
      dots.className = 'coastd-immersive-media-nav__dots';

      this.dots = this.items.map((item, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'coastd-immersive-media-nav__dot';
        dot.setAttribute('aria-label', `Show product media ${index + 1} of ${this.items.length}`);
        dot.addEventListener('click', () => {
          this.slider.scrollTo({ left: item.offsetLeft, behavior: 'smooth' });
        });
        dots.appendChild(dot);
        return dot;
      });

      const count = document.createElement('span');
      count.className = 'coastd-immersive-media-nav__count';
      count.setAttribute('aria-live', 'polite');

      inner.append(dots, count);
      navigation.appendChild(inner);
      this.gallery.appendChild(navigation);

      this.navigation = navigation;
      this.count = count;
      this.update();
    }

    queueUpdate() {
      window.cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = window.requestAnimationFrame(() => this.update());
    }

    update() {
      if (!this.navigation || !this.items?.length) return;

      const sliderLeft = this.slider.getBoundingClientRect().left;
      let activeIndex = 0;
      let shortestDistance = Number.POSITIVE_INFINITY;

      this.items.forEach((item, index) => {
        const distance = Math.abs(item.getBoundingClientRect().left - sliderLeft);
        if (distance < shortestDistance) {
          shortestDistance = distance;
          activeIndex = index;
        }
      });

      this.dots?.forEach((dot, index) => {
        dot.setAttribute('aria-current', index === activeIndex ? 'true' : 'false');
      });

      if (this.count) this.count.textContent = `${activeIndex + 1} / ${this.items.length}`;
    }
  }

  const coastdImmersiveMediaInstances = new WeakMap();

  const initialiseCoastdImmersiveMedia = (scope = document) => {
    const roots = [];
    const selector =
      'product-info.coastd-mobile-immersive-media, product-info.coastd-desktop-editorial';

    if (scope.matches?.(selector)) roots.push(scope);
    scope.querySelectorAll?.(selector).forEach((root) => roots.push(root));

    roots.forEach((root) => {
      coastdImmersiveMediaInstances.get(root)?.destroy();
      coastdImmersiveMediaInstances.set(root, new CoastdImmersiveMedia(root));
    });
  };

  class CoastdDesktopGalleryArrows {
    constructor(root) {
      this.root = root;
      this.gallery = root.querySelector('.coastd-product-media-gallery');
      this.viewer = root.querySelector('.coastd-product-media-viewer');
      this.track = root.querySelector('.coastd-product-media-track');
      this.desktopQuery = window.matchMedia('(min-width: 990px)');
      this.enabled = root.dataset.coastdDesktopArrows !== 'false';
      this.refreshTimer = null;

      if (!this.gallery || !this.viewer || !this.track) return;

      this.handleMediaChange = this.refresh.bind(this);
      this.handleThumbnailClick = this.queueRefresh.bind(this);

      this.desktopQuery.addEventListener?.('change', this.handleMediaChange);
      this.gallery.addEventListener('click', this.handleThumbnailClick);

      this.observer = new MutationObserver(() => this.queueRefresh());
      this.observer.observe(this.track, {
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'hidden'],
        childList: true,
      });

      this.refresh();
    }

    destroy() {
      this.desktopQuery?.removeEventListener?.('change', this.handleMediaChange);
      this.gallery?.removeEventListener('click', this.handleThumbnailClick);
      this.observer?.disconnect();
      window.clearTimeout(this.refreshTimer);
      this.navigation?.remove();
    }

    get items() {
      return [...this.track.querySelectorAll(':scope > .coastd-product-media-slide')].filter(
        (item) => !item.hidden && !item.classList.contains('is-filtered')
      );
    }

    get activeIndex() {
      const items = this.items;
      const index = items.findIndex((item) => item.classList.contains('is-active'));
      return index >= 0 ? index : 0;
    }

    queueRefresh() {
      window.clearTimeout(this.refreshTimer);
      this.refreshTimer = window.setTimeout(() => this.refreshState(), 80);
    }

    createButton(direction, label, path) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `coastd-gallery-arrow coastd-gallery-arrow--${direction}`;
      button.setAttribute('aria-label', label);
      button.innerHTML = `
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="${path}" stroke="currentColor" stroke-width="2.2"
            stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `;
      return button;
    }

    build() {
      this.navigation?.remove();

      const navigation = document.createElement('div');
      navigation.className = 'coastd-gallery-arrows quick-add-hidden';
      navigation.setAttribute('aria-label', 'Product image navigation');

      this.previousButton = this.createButton(
        'prev',
        'Previous product image',
        'M14.5 5.5 8 12l6.5 6.5'
      );
      this.nextButton = this.createButton(
        'next',
        'Next product image',
        'M9.5 5.5 16 12l-6.5 6.5'
      );

      this.previousButton.addEventListener('click', () => this.change(-1));
      this.nextButton.addEventListener('click', () => this.change(1));

      navigation.append(this.previousButton, this.nextButton);
      this.viewer.appendChild(navigation);
      this.navigation = navigation;
    }

    change(direction) {
      const items = this.items;
      if (items.length <= 1) return;

      const nextIndex = (this.activeIndex + direction + items.length) % items.length;
      const target = items[nextIndex];
      const mediaId = target?.dataset.mediaId;

      if (!mediaId) return;

      if (typeof this.gallery.setActiveMedia === 'function') {
        this.gallery.setActiveMedia(mediaId, false);
      } else {
        items.forEach((item) => item.classList.remove('is-active'));
        target.classList.add('is-active');
        this.track.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
      }

      window.setTimeout(() => this.refreshState(), 120);
    }

    refreshState() {
      const items = this.items;

      if (
        !this.enabled ||
        !this.desktopQuery.matches ||
        items.length <= 1
      ) {
        this.navigation?.remove();
        this.navigation = null;
        return;
      }

      if (!this.navigation || !this.viewer.contains(this.navigation)) this.build();

      const current = this.activeIndex + 1;
      const total = items.length;
      this.previousButton?.setAttribute(
        'aria-label',
        `Previous product image. Currently ${current} of ${total}`
      );
      this.nextButton?.setAttribute(
        'aria-label',
        `Next product image. Currently ${current} of ${total}`
      );
    }

    refresh() {
      this.refreshState();
    }
  }

  const coastdDesktopGalleryArrowsInstances = new WeakMap();

  const initialiseCoastdDesktopGalleryArrows = (scope = document) => {
    const selector = 'product-info.coastd-desktop-editorial';
    const roots = [];

    if (scope.matches?.(selector)) roots.push(scope);
    scope.querySelectorAll?.(selector).forEach((root) => roots.push(root));

    [...new Set(roots)].forEach((root) => {
      coastdDesktopGalleryArrowsInstances.get(root)?.destroy();
      coastdDesktopGalleryArrowsInstances.set(root, new CoastdDesktopGalleryArrows(root));
    });
  };


  const addBusinessDays = (date, days) => {
    const result = new Date(date);
    let remaining = Math.max(0, Number(days) || 0);

    while (remaining > 0) {
      result.setDate(result.getDate() + 1);
      const weekday = result.getDay();
      if (weekday !== 0 && weekday !== 6) remaining -= 1;
    }
    return result;
  };

  const formatDispatchDate = (date) =>
    new Intl.DateTimeFormat('en-AU', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    }).format(date);

  const initialiseDispatchEstimates = (scope = document) => {
    scope.querySelectorAll?.('[data-coastd-dispatch-estimate]').forEach((element) => {
      const minDays = Number(element.dataset.minDays || 2);
      const maxDays = Math.max(minDays, Number(element.dataset.maxDays || 4));

      try {
        const now = new Date();
        const earliest = addBusinessDays(now, minDays);
        const latest = addBusinessDays(now, maxDays);
        element.textContent = `${formatDispatchDate(earliest)} – ${formatDispatchDate(latest)}`;
      } catch (error) {
        element.textContent = element.dataset.fallback || 'Usually dispatches in 2–4 business days';
      }
    });
  };

  class CoastdVerticalDesktopGallery {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('.coastd-product-media-track');
      this.gallery = root.querySelector('.coastd-product-media-gallery');
      this.desktopQuery = window.matchMedia('(min-width: 990px)');
      this.frame = null;

      if (!this.track || !this.gallery) return;

      this.handleScroll = this.queueUpdate.bind(this);
      this.track.addEventListener('scroll', this.handleScroll, { passive: true });
      this.update();
    }

    destroy() {
      this.track?.removeEventListener('scroll', this.handleScroll);
      window.cancelAnimationFrame(this.frame);
    }

    queueUpdate() {
      window.cancelAnimationFrame(this.frame);
      this.frame = window.requestAnimationFrame(() => this.update());
    }

    update() {
      if (!this.desktopQuery.matches) return;

      const items = [...this.track.querySelectorAll(':scope > .coastd-product-media-slide')]
        .filter((item) => !item.hidden && !item.classList.contains('is-filtered'));
      if (!items.length) return;

      const trackTop = this.track.getBoundingClientRect().top;
      let active = items[0];
      let distance = Infinity;

      items.forEach((item) => {
        const current = Math.abs(item.getBoundingClientRect().top - trackTop);
        if (current < distance) {
          distance = current;
          active = item;
        }
      });

      items.forEach((item) => item.classList.toggle('is-active', item === active));

      const mediaId = active.dataset.mediaId;
      if (!mediaId) return;

      this.gallery.querySelectorAll('[data-target]').forEach((thumbnail) => {
        const isCurrent = thumbnail.dataset.target?.endsWith(mediaId);
        if (isCurrent) thumbnail.setAttribute('aria-current', 'true');
        else thumbnail.removeAttribute('aria-current');
      });
    }
  }

  const coastdVerticalGalleryInstances = new WeakMap();

  const initialiseCoastdVerticalDesktopGallery = (scope = document) => {
    const selector = 'product-info.coastd-desktop-gallery-vertical';
    const roots = [];

    if (scope.matches?.(selector)) roots.push(scope);
    scope.querySelectorAll?.(selector).forEach((root) => roots.push(root));

    [...new Set(roots)].forEach((root) => {
      coastdVerticalGalleryInstances.get(root)?.destroy();
      coastdVerticalGalleryInstances.set(root, new CoastdVerticalDesktopGallery(root));
    });
  };

  class CoastdShippingProgress extends HTMLElement {
    connectedCallback() {
      this.threshold = Number(this.dataset.threshold || 0);
      this.currency = this.dataset.currency || 'AUD';
      this.message = this.querySelector('.coastd-shipping-progress__message');
      this.fill = this.querySelector('.coastd-shipping-progress__fill');
      this.update(Number(this.dataset.cartTotal || 0));

      if (pubSubAvailable() && PUB_SUB_EVENTS.cartUpdate) {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.refresh());
      }
    }

    disconnectedCallback() {
      this.unsubscribe?.();
    }

    async refresh() {
      try {
        const cartBase = typeof routes !== 'undefined' && routes.cart_url ? routes.cart_url : '/cart';
        const response = await fetch(`${cartBase}.js`, { headers: { Accept: 'application/json' } });
        if (!response.ok) return;
        const cart = await response.json();
        this.update(Number(cart.total_price || 0));
      } catch (error) {
        console.error("Coast'd shipping progress:", error);
      }
    }

    update(cartTotal) {
      if (!this.threshold || !this.message || !this.fill) return;
      const remaining = Math.max(this.threshold - cartTotal, 0);
      const percent = Math.min((cartTotal / this.threshold) * 100, 100);
      const showAmount = this.dataset.showAmount === 'true';

      if (remaining > 0) {
        const amount = moneyFormatter(this.currency).format(remaining / 100);
        this.message.innerHTML = showAmount
          ? `${this.dataset.prefix || 'Add'} <strong>${amount}</strong> ${this.dataset.suffix || 'more for free shipping'}`
          : `${this.dataset.prefix || 'Add'} ${this.dataset.suffix || 'more for free shipping'}`;
      } else {
        this.message.textContent = this.dataset.unlocked || 'Free shipping unlocked';
      }
      this.fill.style.width = `${percent}%`;
    }
  }

  class CoastdMobileReadMore extends HTMLElement {
    connectedCallback() {
      this.content = this.querySelector('.coastd-mobile-read-more__content');
      this.button = this.querySelector('.coastd-mobile-read-more__button');
      this.details = this.closest('details');
      this.previewHeight = Number(this.dataset.previewHeight || 260);
      this.mobileQuery = window.matchMedia('(max-width: 749px)');

      if (!this.content || !this.button) return;

      this.handleButtonClick = this.toggleExpanded.bind(this);
      this.handleDetailsToggle = this.onDetailsToggle.bind(this);
      this.handleMediaChange = this.refresh.bind(this);
      this.handleResize = this.queueRefresh.bind(this);

      this.button.addEventListener('click', this.handleButtonClick);
      this.details?.addEventListener('toggle', this.handleDetailsToggle);
      this.mobileQuery.addEventListener?.('change', this.handleMediaChange);

      this.resizeObserver = new ResizeObserver(this.handleResize);
      this.resizeObserver.observe(this.content);

      this.refresh();
      window.setTimeout(() => this.refresh(), 180);
    }

    disconnectedCallback() {
      this.button?.removeEventListener('click', this.handleButtonClick);
      this.details?.removeEventListener('toggle', this.handleDetailsToggle);
      this.mobileQuery?.removeEventListener?.('change', this.handleMediaChange);
      this.resizeObserver?.disconnect();
      window.cancelAnimationFrame(this.refreshFrame);
    }

    queueRefresh() {
      window.cancelAnimationFrame(this.refreshFrame);
      this.refreshFrame = window.requestAnimationFrame(() => this.refresh());
    }

    onDetailsToggle() {
      if (!this.details?.open) {
        this.setExpanded(false, false);
        return;
      }
      window.requestAnimationFrame(() => this.refresh());
    }

    refresh() {
      if (!this.content || !this.button) return;

      this.style.setProperty('--coastd-mobile-preview-height', `${this.previewHeight}px`);

      if (!this.mobileQuery.matches) {
        this.classList.remove('is-needed', 'is-expanded');
        this.button.hidden = true;
        this.button.setAttribute('aria-expanded', 'false');
        this.content.style.removeProperty('max-height');
        return;
      }

      const fullHeight = this.content.scrollHeight;
      const needsButton = fullHeight > this.previewHeight + 24;
      this.classList.toggle('is-needed', needsButton);
      this.button.hidden = !needsButton;

      if (!needsButton) {
        this.setExpanded(false, false);
        return;
      }

      if (this.classList.contains('is-expanded')) {
        this.style.setProperty('--coastd-mobile-expanded-height', `${fullHeight}px`);
      }
    }

    toggleExpanded() {
      this.setExpanded(!this.classList.contains('is-expanded'), true);
    }

    setExpanded(expanded, animate) {
      if (!this.content || !this.button) return;

      const label = this.button.querySelector('[data-coastd-read-more-label]');
      const moreLabel = this.button.dataset.moreLabel || 'Read more';
      const lessLabel = this.button.dataset.lessLabel || 'Show less';

      if (expanded) {
        this.style.setProperty('--coastd-mobile-expanded-height', `${this.content.scrollHeight}px`);
      } else if (animate && this.classList.contains('is-expanded')) {
        this.style.setProperty('--coastd-mobile-expanded-height', `${this.content.scrollHeight}px`);
        this.content.getBoundingClientRect();
      }

      this.classList.toggle('is-expanded', expanded);
      this.button.setAttribute('aria-expanded', String(expanded));
      if (label) label.textContent = expanded ? lessLabel : moreLabel;

      if (!expanded && animate) {
        this.button.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
  }

  class CoastdStickyAtc extends HTMLElement {
    connectedCallback() {
      if (this.dataset.coastdPortaled !== 'true') {
        const key = this.dataset.stickyKey;
        if (key) {
          document
            .querySelectorAll(`body > coastd-sticky-atc[data-sticky-key="${CSS.escape(key)}"]`)
            .forEach((existing) => {
              if (existing !== this) existing.remove();
            });
        }

        this.dataset.coastdPortaled = 'true';
        document.body.appendChild(this);
        return;
      }

      if (this.dataset.coastdInitialised === 'true') return;
      this.dataset.coastdInitialised = 'true';
      this.hidden = false;
      this.initialise();
    }

    initialise() {
      this.variants = this.parseJson('[data-coastd-variants-json]', []);
      this.optionNames = this.parseJson('[data-coastd-options-json]', []);
      this.quickOptionPosition = Math.max(1, Number(this.dataset.optionPosition || 1));
      this.quickOptionIndex = this.quickOptionPosition - 1;
      this.quickOptionName = this.dataset.optionName || this.optionNames[this.quickOptionIndex] || '';

      this.actionButton = this.querySelector('[data-coastd-sticky-button]');
      this.mobileTrigger = this.querySelector('[data-coastd-mobile-trigger]');
      this.sheet = this.querySelector('[data-coastd-quick-sheet]');
      this.sheetSubmit = this.querySelector('[data-coastd-sheet-submit]');
      this.variantText = this.querySelector('.coastd-quick-sheet__variant');
      this.priceText = this.querySelector('.coastd-quick-sheet__price');
      this.image = this.querySelector('.coastd-quick-sheet__image');
      this.busy = false;

      this.refreshSourceReferences();

      this.actionButton?.addEventListener('click', () => this.handleSubmit());
      this.mobileTrigger?.addEventListener('click', () => this.handleMobileTrigger());
      this.sheetSubmit?.addEventListener('click', () => this.handleSubmit(true));

      this.querySelectorAll('[data-coastd-sheet-close]').forEach((button) => {
        button.addEventListener('click', () => this.closeSheet());
      });

      this.querySelector('[data-coastd-size-guide]')?.addEventListener('click', () => {
        this.closeSheet();
        this.openSizeGuide();
      });

      this.querySelectorAll('[data-coastd-sticky-option]').forEach((button) => {
        button.addEventListener('click', () => this.selectQuickOption(button));
      });

      this.handleKeydown = (event) => {
        if (event.key === 'Escape' && !this.sheet?.hidden) this.closeSheet();
      };
      document.addEventListener('keydown', this.handleKeydown);

      this.setupVisibilityObserver();
      this.syncFromNativePicker();
      this.syncAvailability();
    }

    disconnectedCallback() {
      this.intersectionObserver?.disconnect();
      this.buttonObserver?.disconnect();
      document.removeEventListener('keydown', this.handleKeydown);
      document.body.classList.remove('coastd-sticky-visible');
      document.body.classList.remove('coastd-quick-sheet-open');
    }

    parseJson(selector, fallback) {
      try {
        return JSON.parse(this.querySelector(selector)?.textContent || JSON.stringify(fallback));
      } catch (error) {
        console.warn('Coastd quick add JSON could not be parsed.', error);
        return fallback;
      }
    }

    refreshSourceReferences() {
      this.sourceButton = document.getElementById(this.dataset.sourceButtonId);
      this.productInfo =
        document.getElementById(this.dataset.productInfoId) ||
        this.sourceButton?.closest('product-info');
      this.productForm =
        document.getElementById(this.dataset.productFormId) ||
        this.sourceButton?.closest('form');
      this.variantIdInput =
        this.productForm?.querySelector('input.product-variant-id[name="id"]') ||
        this.productInfo?.querySelector('input.product-variant-id[name="id"]');
      this.observedTarget =
        this.sourceButton?.closest('.product-form__buttons') || this.sourceButton;
    }

    setupVisibilityObserver() {
      this.refreshSourceReferences();
      if (!this.observedTarget) {
        window.setTimeout(() => this.setupVisibilityObserver(), 150);
        return;
      }

      if (this.dataset.trigger === 'always') {
        this.setVisible(true);
      } else {
        this.intersectionObserver?.disconnect();
        this.intersectionObserver = new IntersectionObserver(
          ([entry]) => {
            const hasPassedButtons =
              !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
            this.setVisible(hasPassedButtons);
          },
          { threshold: 0, rootMargin: '0px 0px -1px 0px' }
        );
        this.intersectionObserver.observe(this.observedTarget);
      }

      this.buttonObserver?.disconnect();
      this.buttonObserver = new MutationObserver(() => this.syncAvailability());
      this.buttonObserver.observe(this.sourceButton, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled'],
        childList: true,
        subtree: true,
      });
    }

    setVisible(visible) {
      this.classList.toggle('is-visible', visible);
      document.body.classList.toggle('coastd-sticky-visible', visible);
    }

    handleMobileTrigger() {
      if (this.dataset.hasVariants === 'true') {
        this.openSheet();
      } else {
        this.handleSubmit();
      }
    }

    openSheet() {
      if (!this.sheet) return;
      this.syncFromNativePicker();
      this.sheet.hidden = false;
      document.body.classList.add('coastd-quick-sheet-open');

      requestAnimationFrame(() => {
        this.sheet.classList.add('is-open');
        this.sheet.querySelector('.coastd-quick-sheet__close')?.focus();
      });
    }

    closeSheet() {
      if (!this.sheet || this.sheet.hidden) return;

      this.sheet.classList.remove('is-open');
      document.body.classList.remove('coastd-quick-sheet-open');

      window.setTimeout(() => {
        if (!this.sheet.classList.contains('is-open')) {
          this.sheet.hidden = true;
        }
      }, 430);
    }

    selectQuickOption(button) {
      if (this.busy || button.disabled) return;

      const value = button.dataset.optionValue || button.textContent.trim();
      const selectedValues = this.getCurrentOptionValues();
      selectedValues[this.quickOptionIndex] = value;

      const variant = this.findVariant(selectedValues);
      this.setPressedValue(value);

      if (!variant) {
        this.setVariantUnavailable();
        return;
      }

      this.applyVariantDirectly(variant);
      this.mirrorToNativePicker(value);
    }

    getCurrentOptionValues() {
      this.refreshSourceReferences();

      return this.optionNames.map((name, index) => {
        if (index === this.quickOptionIndex) {
          const pressed = this.querySelector(
            '[data-coastd-sticky-option][aria-pressed="true"]'
          );
          if (pressed) return pressed.dataset.optionValue || pressed.textContent.trim();
        }

        const escapedName = CSS.escape(name);
        const input =
          this.productInfo?.querySelector(`input[name="options[${escapedName}]"]:checked`) ||
          this.productInfo?.querySelector(`select[name="options[${escapedName}]"]`);

        if (input?.matches('select')) return input.value;
        if (input) return input.value;

        const selectedVariant = this.getVariantFromInput();
        return selectedVariant?.options?.[index] || '';
      });
    }

    getVariantFromInput() {
      const id = Number(this.variantIdInput?.value);
      return this.variants.find((variant) => Number(variant.id) === id);
    }

    findVariant(values) {
      return this.variants.find((variant) =>
        variant.options.every((optionValue, index) => optionValue === values[index])
      );
    }

    applyVariantDirectly(variant) {
      this.refreshSourceReferences();

      if (this.variantIdInput) {
        this.variantIdInput.value = variant.id;
        this.variantIdInput.disabled = !variant.available;
        this.variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      this.currentVariant = variant;
      this.updateVariantUi(variant);
    }

    mirrorToNativePicker(value) {
      this.refreshSourceReferences();
      const escapedName = CSS.escape(this.quickOptionName);
      const candidates = [
        ...(
          this.productInfo?.querySelectorAll(
            `input[name="options[${escapedName}]"], select[name="options[${escapedName}]"]`
          ) || []
        ),
      ];

      const input = candidates.find(
        (element) => element.matches('input') && element.value === value
      );
      const select = candidates.find((element) => element.matches('select'));

      if (input && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      } else if (select && select.value !== value) {
        select.value = value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    syncFromNativePicker() {
      this.refreshSourceReferences();
      const variant = this.getVariantFromInput();
      if (!variant) return;

      const value = variant.options?.[this.quickOptionIndex];
      if (value) this.setPressedValue(value);
      this.currentVariant = variant;
      this.updateVariantUi(variant);
    }

    setPressedValue(value) {
      this.querySelectorAll('[data-coastd-sticky-option]').forEach((button) => {
        button.setAttribute(
          'aria-pressed',
          String((button.dataset.optionValue || button.textContent.trim()) === value)
        );
      });
    }

    setVariantUnavailable() {
      [this.actionButton, this.mobileTrigger, this.sheetSubmit].forEach((button) => {
        if (!button) return;
        button.disabled = true;
        button.textContent = this.dataset.soldOutLabel || 'Unavailable';
      });
    }

    updateVariantUi(variant) {
      const title = variant.title || '';
      if (this.variantText) {
        this.variantText.textContent = title === 'Default Title' ? '' : title;
        this.variantText.hidden = title === 'Default Title';
      }

      if (this.priceText && Number.isFinite(variant.price)) {
        if (window.Shopify?.formatMoney) {
          this.priceText.textContent = window.Shopify.formatMoney(variant.price);
        }
      }

      if (this.image && variant.featured_image?.src) {
        this.image.src = variant.featured_image.src;
      }

      const label = variant.available
        ? this.dataset.addLabel
        : this.dataset.soldOutLabel || 'Sold out';

      [this.actionButton, this.mobileTrigger, this.sheetSubmit].forEach((button) => {
        if (!button) return;
        button.disabled = !variant.available || this.busy;
        button.textContent = label;
      });
    }

    syncAvailability() {
      this.refreshSourceReferences();
      const variant = this.getVariantFromInput() || this.currentVariant;
      if (variant) {
        this.updateVariantUi(variant);
      } else {
        const disabled =
          this.sourceButton?.disabled ||
          this.sourceButton?.getAttribute('aria-disabled') === 'true';
        [this.actionButton, this.mobileTrigger, this.sheetSubmit].forEach((button) => {
          if (button) button.disabled = Boolean(disabled);
        });
      }
    }

    async handleSubmit(fromSheet = false) {
      if (this.busy) return;

      this.refreshSourceReferences();
      const variant = this.getVariantFromInput() || this.currentVariant;

      if (this.dataset.hasVariants === 'true' && !variant) {
        if (window.matchMedia('(max-width: 749px)').matches) this.openSheet();
        else this.scrollToOptions();
        return;
      }

      if (!variant?.available && this.dataset.hasVariants === 'true') return;

      this.busy = true;
      if (fromSheet) this.closeSheet();
      else document.body.classList.remove('coastd-quick-sheet-open');

      [this.actionButton, this.mobileTrigger, this.sheetSubmit].forEach((button) => {
        if (button) button.disabled = true;
      });

      await new Promise((resolve) => requestAnimationFrame(resolve));
      this.refreshSourceReferences();

      try {
        if (this.productForm?.requestSubmit && this.sourceButton) {
          this.productForm.requestSubmit(this.sourceButton);
        } else if (this.sourceButton) {
          this.sourceButton.click();
        }
      } finally {
        window.setTimeout(() => {
          this.busy = false;
          this.syncAvailability();
        }, 1200);
      }
    }

    scrollToOptions() {
      this.refreshSourceReferences();
      const target =
        this.productInfo?.querySelector('variant-selects') ||
        this.productInfo?.querySelector('variant-radios') ||
        this.productInfo?.querySelector('.product-form__input') ||
        this.sourceButton;

      target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    openSizeGuide() {
      this.refreshSourceReferences();
      const sizeGuide =
        this.productInfo?.querySelector('[data-coastd-size-chart-trigger]') ||
        [...(this.productInfo?.querySelectorAll('summary, button') || [])]
          .find((element) => /size guide/i.test(element.textContent || ''));

      if (sizeGuide) {
        sizeGuide.click();
        sizeGuide.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }

  if (!customElements.get('coastd-sticky-atc')) {
    customElements.define('coastd-sticky-atc', CoastdStickyAtc);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initialiseCoastdImmersiveMedia();
      initialiseCoastdDesktopGalleryArrows();
      initialiseCoastdVerticalDesktopGallery();
      initialiseDispatchEstimates();
    });
  } else {
    initialiseCoastdImmersiveMedia();
    initialiseCoastdDesktopGalleryArrows();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initialiseCoastdImmersiveMedia(event.target);
    initialiseCoastdDesktopGalleryArrows(event.target);
    initialiseCoastdVerticalDesktopGallery(event.target);
    initialiseDispatchEstimates(event.target);
  });

  document.addEventListener('product-info:loaded', (event) => {
    const root = event.target.closest?.('product-info') || event.target;
    initialiseCoastdImmersiveMedia(root.parentElement || document);
    initialiseCoastdDesktopGalleryArrows(root.parentElement || document);
  });

  document.addEventListener('click', (event) => {
    const button = event.target.closest('.product-form__submit[data-coastd-success-label]');
    if (!button) return;
    button.dataset.coastdPending = 'true';
    button.dataset.coastdOriginalLabel = button.querySelector('span')?.textContent?.trim() || '';
  });

  if (pubSubAvailable() && PUB_SUB_EVENTS.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      document
        .querySelectorAll('.product-form__submit[data-coastd-pending="true"]')
        .forEach((button) => {
          delete button.dataset.coastdPending;
          const label = button.querySelector('span');
          const success = button.dataset.coastdSuccessLabel;
          const original = button.dataset.coastdOriginalLabel;
          if (!label || !success) return;
          label.textContent = success;
          window.setTimeout(() => {
            if (button.isConnected) label.textContent = original;
          }, 1500);
        });
    });
  }

  if (pubSubAvailable() && PUB_SUB_EVENTS.variantChange) {
    subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
      const variant = data?.variant;
      if (!variant) return;

      window.setTimeout(() => {
        initialiseCoastdImmersiveMedia();
        initialiseCoastdDesktopGalleryArrows();
        initialiseCoastdVerticalDesktopGallery();
      }, 140);

      const src = variant.featured_media?.preview_image?.src;
      if (src) {
        document
          .querySelectorAll(
            '.coastd-camera-card[data-coastd-variant-image="true"] .coastd-camera-card__image'
          )
          .forEach((image) => {
            image.src = src;
            image.removeAttribute('srcset');
          });
      }
    });
  }
})();
